import * as Sentry from '@sentry/react'

export type AgeVerificationOutcome = 'success' | 'fail' | 'spoof' | 'fallback'

type VerificationResponse = {
    success: boolean
    httpStatus?: number
    data?: unknown
    message?: string
    timestamp?: string
    responseBody?: unknown
    error?: {
        code?: string
        category?: string
        message?: string
        reason?: string
        age?: number
    } | null
}

export function getAgeVerificationOutcome(
    response: VerificationResponse
): AgeVerificationOutcome {
    // The vendor owns the threshold. A definitive rejection must never offer
    // manual confirmation, even when no numeric age accompanies the reason.
    if (response.httpStatus === 403 && response.success === false) {
        if (response.error?.reason === 'underage') return 'fail'
        if (response.error?.reason === 'spoof_detection') return 'spoof'
    }
    if (
        response.httpStatus !== undefined &&
        (response.httpStatus < 200 || response.httpStatus >= 300)
    ) {
        return 'fallback'
    }
    const age =
        response.data &&
        typeof response.data === 'object' &&
        'age' in response.data
            ? response.data.age
            : undefined
    if (typeof age !== 'number' || !Number.isFinite(age) || age < 0) {
        return 'fallback'
    }
    if (age < 18) return 'fail'
    return response.success && !response.error ? 'success' : 'fallback'
}

export type IcuOperation = 'activate' | 'verify-age' | 'status' | 'deactivate'

export type IcuCallResult = {
    operation: IcuOperation
    status: 'success' | 'fail'
    age?: number
    reason?: string
}

const responseListeners = new Set<(result: IcuCallResult) => void>()

export function subscribeIcuResponses(
    listener: (result: IcuCallResult) => void
) {
    responseListeners.add(listener)
    return () => {
        responseListeners.delete(listener)
    }
}

// Keep response diagnostics, but never send captured images or credentials.
function sanitizeResponse(value: unknown, depth = 0): unknown {
    if (depth > 8) return '[truncated]'
    if (typeof value === 'string') {
        return value
            .replace(/data:image\/[^\s"']+/gi, '[image omitted]')
            .slice(0, 2000)
    }
    if (Array.isArray(value))
        return value
            .slice(0, 30)
            .map((item) => sanitizeResponse(item, depth + 1))
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([key, item]) => [
                key,
                /image|token|authorization|password|secret|credential|streamUrl/i.test(
                    key
                )
                    ? '[redacted]'
                    : sanitizeResponse(item, depth + 1)
            ])
        )
    }
    return value
}

export function reportIcuResponse(
    operation: IcuOperation,
    response: VerificationResponse,
    traceId?: string,
    cancelled = false
) {
    const outcome =
        operation === 'verify-age'
            ? getAgeVerificationOutcome(response)
            : response.success
              ? 'success'
              : 'fallback'
    const failed = cancelled || outcome !== 'success'
    const code = cancelled
        ? 'CANCELLED'
        : (response.error?.code ??
          (outcome === 'fail'
              ? 'UNDERAGE'
              : outcome === 'spoof'
                ? 'SPOOF_DETECTION'
                : response.error?.reason === 'timeout'
                  ? 'TIMEOUT'
                  : response.success
                    ? 'INVALID_RESPONSE'
                    : 'BUSINESS_FAILURE'))
    const reason = cancelled
        ? 'Request cancelled'
        : response.error?.message ||
          (outcome === 'fail'
              ? response.httpStatus === 403
                  ? 'Detected age is below the required threshold'
                  : 'Detected age is below 18'
              : response.message || code)
    const detectedAge =
        response.httpStatus === 403 && response.error?.reason === 'underage'
            ? response.error.age
            : response.data &&
                typeof response.data === 'object' &&
                'age' in response.data
              ? response.data.age
              : undefined
    const age =
        typeof detectedAge === 'number' &&
        Number.isFinite(detectedAge) &&
        detectedAge >= 0
            ? detectedAge
            : undefined
    const result: IcuCallResult = {
        operation,
        status: failed ? 'fail' : 'success',
        ...(age !== undefined ? { age } : {}),
        ...(failed ? { reason } : {})
    }
    const details = {
        traceId,
        httpStatus: response.httpStatus,
        category: response.error?.category,
        response: JSON.stringify(sanitizeResponse(response))
    }
    Sentry.addBreadcrumb({
        category: 'icu.response',
        message: `ICU ${operation}: ${result.status}`,
        level: failed && !cancelled ? 'error' : 'info',
        data: details
    })
    // Logs make every response searchable even when no error issue occurs.
    Sentry.logger.info(`ICU ${operation} response`, {
        feature: 'age-verification',
        operation,
        status: result.status,
        cancelled,
        ...(traceId ? { traceId } : {}),
        ...(response.httpStatus !== undefined
            ? { httpStatus: response.httpStatus }
            : {}),
        response: details.response
    })
    if (failed && !cancelled) {
        Sentry.captureMessage(`ICU ${operation} failed: ${code}`, {
            level: 'error',
            tags: { feature: 'age-verification', operation, errorCode: code },
            extra: details
        })
    }
    for (const listener of responseListeners) listener(result)
}
